/**
 * Points picked on the Connect wizard's Points step (web brief U5), whatever
 * the browse kind: every view (register table, node tree, tag list, block scan,
 * manual entry) outputs {@link PickedPoint}s, which become Map-step rows.
 *
 * The block-scan rules (S7, MELSEC; design 2026-09-24 section 7.4.1) are a
 * port of the Android app's GatewayScanRules: cursor progress text, grouping by
 * area / data block / device, and de-duplicated appends.
 */

import type { BrowseEntry, PointDataType, ThingDataType } from "@/lib/api/things";
import type { SniffedRegister } from "@/lib/gateway-client";
import { DRIVER, isModbusDriver } from "./drivers";

export const POINT_DATA_TYPES: PointDataType[] = [
  "bool",
  "int16",
  "uint16",
  "int32",
  "uint32",
  "int64",
  "uint64",
  "float32",
  "float64",
  "string",
];

/** A v2 point type → the row editor's data-type facade. */
export function facadeForPointType(t: PointDataType): ThingDataType {
  if (t === "bool") return "boolean";
  if (t === "string") return "text";
  if (t === "float32" || t === "float64") return "number";
  return "integer";
}

/** A point to poll, as a picker produced it. */
export interface PickedPoint {
  /** Unique per device: the address in a canonical form. */
  key: string;
  /** Suggested metric name (renamed on the Map step). */
  metric: string;
  /** The driver's address object, ready to save (browse entries carry it as is). */
  address: Record<string, unknown>;
  data_type: PointDataType;
  /** What the picker showed ("holding 4", "Temperature", "DB1.DBW0"). */
  label: string;
  /** The value read while browsing, when there was one. */
  value?: unknown;
  /** Engineering unit, set on the Points step or by an imported map. */
  unit?: string;
  /** Linear scale the gateway applies (real = raw x scale); undefined => 1. */
  scale?: number;
  /** An imported map's poll interval for this point, whole seconds; undefined => the Map step's default. */
  poll_sec?: number;
  /** An imported map's deadband (publish only when the value moves more than this). */
  deadband?: number;
  /** An imported map's heartbeat, whole seconds (how long the last value reads as current). */
  max_silence_sec?: number;
}

/** A canonical, key-sorted JSON of an address, so one address is one key. */
export function addressKey(address: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(address)
      .sort()
      .reduce<Record<string, unknown>>((o, k) => {
        o[k] = address[k];
        return o;
      }, {}),
  );
}

/** A browse name or node as a metric name: lower case, [a-z0-9_] only. */
export function metricFromName(name: string): string {
  const base = name
    .split(/\s{2,}/)[0] // block-scan names carry the raw value after two spaces
    .replace(/^.*[;:]s=/, "") // an OPC UA string node id
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "point";
}

function isPointType(t: unknown): t is PointDataType {
  return typeof t === "string" && (POINT_DATA_TYPES as string[]).includes(t);
}

/** Map a browse `data_type` spelling the gateway may use onto the platform's enum. */
function normaliseType(t: unknown): PointDataType | undefined {
  if (isPointType(t)) return t;
  if (typeof t !== "string") return undefined;
  const s = t.toLowerCase();
  const aliases: Record<string, PointDataType> = {
    boolean: "bool",
    bit: "bool",
    word: "uint16",
    int: "int16",
    dint: "int32",
    udint: "uint32",
    lint: "int64",
    real: "float32",
    float: "float32",
    lreal: "float64",
    double: "float64",
    byte: "uint16",
    sint: "int16",
    usint: "uint16",
    uint: "uint16",
    text: "string",
  };
  return aliases[s];
}

/** The type a point defaults to when browse gave none: bits read as bool, words as int16. */
function fallbackType(driver: string, address: Record<string, unknown>): PointDataType {
  const space = address.space;
  if (space === "coil" || space === "discrete") return "bool";
  if (driver === DRIVER.MELSEC && typeof address.device === "string" && /^(X|Y|M|B|SB|L|F|V)/i.test(address.device)) {
    return "bool";
  }
  if (driver === DRIVER.S7 && typeof address.bit === "number") return "bool";
  return "int16";
}

/**
 * A browse entry as a point, or null when it is a branch with no address.
 * The data type is the entry's (normalised), else the driver's fallback, and
 * always one the driver can decode when that list is known.
 */
export function browseEntryToPoint(
  driver: string,
  entry: BrowseEntry,
  decodable?: readonly PointDataType[],
): PickedPoint | null {
  const address = entry.address;
  if (!address || typeof address !== "object" || Object.keys(address).length === 0) return null;
  let type = normaliseType(entry.data_type) ?? fallbackType(driver, address);
  if (decodable && decodable.length > 0 && !decodable.includes(type)) type = decodable[0];
  let metric = metricFromName(entry.name);
  if (isModbusDriver(driver) && typeof address.register === "number") {
    const space = typeof address.space === "string" ? address.space : "holding";
    metric = `${SPACE_PREFIX[space] ?? "reg"}_${address.register}`;
  }
  return { key: addressKey(address), metric, address, data_type: type, label: entry.name, value: entry.value };
}

const SPACE_PREFIX: Record<string, string> = { holding: "hr", input: "ir", coil: "coil", discrete: "di" };

/**
 * A Modbus browse entry as a register row for the existing register table and
 * Map editor (so a v2 gateway's browse and an old gateway's sniff produce the
 * same rows). Null when the entry has no register address.
 */
export function modbusEntryToRegister(entry: BrowseEntry): SniffedRegister | null {
  const a = entry.address;
  const register = typeof a?.register === "number" ? a.register : Number(a?.register);
  if (!a || !Number.isInteger(register)) return null;
  const space = (typeof a.space === "string" && a.space ? a.space : "holding") as NonNullable<SniffedRegister["space"]>;
  const bits = space === "coil" || space === "discrete";
  const raw = entry.value;
  const value = typeof raw === "number" ? raw : typeof raw === "boolean" ? (raw ? 1 : 0) : typeof raw === "string" ? raw : "—";
  return {
    address: register,
    space,
    data_type: "int16",
    reg_count: 1,
    value,
    guessed_name: `${SPACE_PREFIX[space] ?? "reg"}_${register}`,
    unsigned: !bits && normaliseType(entry.data_type) === "uint16",
  };
}

/** The selection id of a register row: the address, prefixed by its space unless holding (old keys unchanged). */
export function registerKey(r: Pick<SniffedRegister, "address" | "space">): string {
  return r.space && r.space !== "holding" ? `${r.space}:${r.address}` : String(r.address);
}

/**
 * A short label for a point address: "DB1 byte 4", "M byte 2.3", "D100",
 * "ns=2;i=1001", "Temperature[3]", "holding 4", "active_clay_content" (a
 * results feed key).
 */
export function pointAddressLabel(a: Record<string, unknown> | null | undefined): string {
  if (!a || Object.keys(a).length === 0) return "no address";
  if (typeof a.node_id === "string") return a.node_id;
  if (typeof a.device === "string") return a.device;
  if (typeof a.tag === "string") return a.elem !== undefined ? `${a.tag}[${String(a.elem)}]` : a.tag;
  if (typeof a.area === "string") {
    const where = a.area === "DB" ? `DB${String(a.db ?? "?")}` : a.area;
    return `${where} byte ${String(a.byte ?? "?")}${a.bit !== undefined ? `.${String(a.bit)}` : ""}`;
  }
  if (a.register !== undefined) return `${typeof a.space === "string" ? a.space : "holding"} ${String(a.register)}`;
  if (typeof a.key === "string") return a.key;
  return JSON.stringify(a);
}

/** A browse value as text: strings unquoted, absent as a dash. */
export function valueText(v: unknown): string {
  if (v === undefined || v === null) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/\.?0+$/, "");
  if (typeof v === "string" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

// ── Block scan (S7, MELSEC) ───────────────────────────────────────────────

/** The walk's default end per MELSEC device (design 7.4.1). */
const MELSEC_DEFAULT_END: Record<string, number> = { D: 2048, W: 0x800, R: 2048, M: 2048, X: 0x400, Y: 0x400, B: 0x800 };
const MELSEC_HEX = new Set(["X", "Y", "W", "B"]);
const MELSEC_TWO_LETTER = new Set(["SB", "SW", "SM", "SD", "DX", "DY", "ZR", "TN", "TS", "TC", "CN", "CS", "CC", "LZ"]);
/** S7 block size (bytes) when a cursor does not say; the gateway's default. */
const S7_DEFAULT_BLOCK = 64;

/** The first cursor of a walk: empty (only where data lives), or skip_zero:false (every word and bit). */
export function firstBlockCursor(showZeros: boolean): Record<string, unknown> {
  return showZeros ? { skip_zero: false } : {};
}

const str = (o: Record<string, unknown> | null | undefined, k: string): string | undefined =>
  typeof o?.[k] === "string" ? (o[k] as string) : undefined;
const int = (o: Record<string, unknown> | null | undefined, k: string): number | undefined =>
  typeof o?.[k] === "number" && Number.isInteger(o[k]) ? (o[k] as number) : undefined;

export function blockDriverName(driver: string): string {
  return driver === DRIVER.S7 ? "S7" : driver === DRIVER.MELSEC ? "MELSEC" : driver;
}

/**
 * Where a block-scan cursor points, in words: "S7 · DB12 bytes 256–319",
 * "MELSEC · D 1024/2048", "MELSEC · X 0x100/0x400" (X, Y, W and B are hex).
 * The gateway omits fields at their defaults, so a missing S7 count or MELSEC
 * end is the documented default.
 */
export function blockProgress(driver: string, cursor: Record<string, unknown> | null | undefined): string {
  const name = blockDriverName(driver);
  if (!cursor) return name;
  if (driver === DRIVER.S7) {
    const area = str(cursor, "area");
    if (!area) return `${name} · starting at DB1`;
    const where = area === "DB" ? `DB${int(cursor, "db") ?? 1}` : area;
    const start = int(cursor, "start");
    if (start === undefined) return `${name} · ${where}`;
    const count = int(cursor, "count") || S7_DEFAULT_BLOCK;
    const end = int(cursor, "end");
    const last = Math.min(start + count, end ?? Infinity) - 1;
    return `${name} · ${where} bytes ${start}–${Math.max(start, last)}`;
  }
  if (driver === DRIVER.MELSEC) {
    const device = str(cursor, "device");
    if (!device) return `${name} · starting at D0`;
    const up = device.toUpperCase();
    const n = (v: number) => (MELSEC_HEX.has(up) ? `0x${v.toString(16).toUpperCase()}` : String(v));
    const start = int(cursor, "start") ?? 0;
    const end = int(cursor, "end") ?? MELSEC_DEFAULT_END[up];
    return end !== undefined ? `${name} · ${device} ${n(start)}/${n(end)}` : `${name} · ${device} from ${n(start)}`;
  }
  return name;
}

function melsecDevice(device: string): string {
  const up = device.toUpperCase();
  return MELSEC_TWO_LETTER.has(up.slice(0, 2)) ? up.slice(0, 2) : up.slice(0, 1);
}

/** Which group a block-scan entry belongs to: S7 by area, each DB on its own; MELSEC by device. */
export function blockGroupKey(driver: string, entry: BrowseEntry): string {
  const a = entry.address;
  if (driver === DRIVER.S7) {
    const area = str(a, "area");
    if (area === "DB") return `DB${int(a, "db") ?? 0}`;
    return area ?? entry.name.split(".")[0];
  }
  if (driver === DRIVER.MELSEC) return melsecDevice(str(a, "device") ?? entry.name);
  return "";
}

export function blockGroupLabel(driver: string, key: string): string {
  const s7: Record<string, string> = { M: "markers", I: "inputs", Q: "outputs" };
  const melsec: Record<string, string> = {
    D: "data registers",
    W: "link registers",
    R: "file registers",
    M: "internal relays",
    X: "inputs",
    Y: "outputs",
    B: "link relays",
  };
  const what =
    driver === DRIVER.S7 ? (s7[key] ?? (key.startsWith("DB") ? "data block" : undefined)) : driver === DRIVER.MELSEC ? melsec[key] : undefined;
  return what ? `${key} · ${what}` : key;
}

/** One area, data block or device of a block scan, in walk order. */
export interface EntryGroup {
  key: string;
  label: string;
  entries: BrowseEntry[];
}

export interface BlockAppend {
  groups: EntryGroup[];
  /** New entries kept (repeats of listed points are dropped). */
  added: number;
  /** The page held more new entries than there was room for. */
  truncated: boolean;
}

/**
 * Append one block-scan page, keeping walk order: an entry joins its group, a
 * new group goes last. An entry already listed (same group and address) is
 * dropped, so a block read twice cannot duplicate rows. At most `room` new
 * entries are kept. `seen` is updated in place.
 */
export function appendBlockPage(
  groups: EntryGroup[],
  page: BrowseEntry[],
  driver: string,
  seen: Set<string>,
  room: number,
): BlockAppend {
  const out = groups.map((g) => ({ ...g, entries: [...g.entries] }));
  const index = new Map(out.map((g, i) => [g.key, i]));
  let added = 0;
  let truncated = false;
  for (const e of page) {
    const key = blockGroupKey(driver, e);
    const id = `${key}/${e.address ? addressKey(e.address) : e.name}`;
    if (seen.has(id)) continue;
    if (added >= room) {
      truncated = true;
      break;
    }
    seen.add(id);
    const i = index.get(key);
    if (i !== undefined) out[i].entries.push(e);
    else {
      index.set(key, out.length);
      out.push({ key, label: blockGroupLabel(driver, key), entries: [e] });
    }
    added++;
  }
  return { groups: out, added, truncated };
}
